import { Components, CSSObject, Theme } from '@mui/material'
import { DISABLED_OPACITY } from './disabledOpacity'
import {
  IconButtonColour,
  iconButtonColourStyles,
  iconButtonIconStyle,
  iconButtonSizeStyles,
  IconButtonVariant,
} from './iconButtonStyles'

declare module '@mui/material/ToggleButton' {
  interface ToggleButtonOwnProps {
    // ToggleButton doesn't have variants in MUI, so adding them is different
    variant?: IconButtonVariant
  }
}

const colourStyle = (theme: Theme, variant: IconButtonVariant, colour: IconButtonColour): CSSObject => {
  const { base, hover, disabled } = iconButtonColourStyles(theme)[variant][colour]
  // Figma marks selection with a filled icon and leaves the container alone; ghost deviates and takes
  // the active background, since a transparent container has nothing else to carry the state
  const selectedBackground = variant === 'ghost' ? theme.palette.bg.interactive.active : base.backgroundColor
  const selectedHoverBackground = variant === 'ghost' ? selectedBackground : (hover.backgroundColor ?? base.backgroundColor)

  return {
    ...base,
    '&:hover': hover,
    '&.Mui-disabled': disabled,
    // MUI tints the selected state from palette[color].main, which would drop the omui colours
    '&.Mui-selected': {
      ...base,
      backgroundColor: selectedBackground,
      '&:hover': { ...hover, backgroundColor: selectedHoverBackground },
    },
  }
}

export const MuiToggleButton: Components<Omit<Theme, 'components'>>['MuiToggleButton'] = {
  defaultProps: {
    disableRipple: true,
    variant: 'ghost',
    color: 'secondary',
  },
  styleOverrides: {
    root: ({ theme }) => ({
      textTransform: 'none',
      // MUI outlines every toggle with palette.divider; omui wants that only where a group joins
      // buttons into one outlined block, and the group's -1px collapse margins assume a 1px border
      border: 'none',
      '&.MuiToggleButtonGroup-grouped': {
        border: `${theme.strokeWidth.strong} solid ${theme.palette.border.subtle}`,
      },
      '&.Mui-disabled': {
        opacity: DISABLED_OPACITY,
      },
      ...iconButtonIconStyle,
    }),
  },
  variants: [
    {
      props: { variant: 'contained' },
      style: {},
    },
    {
      props: { variant: 'ghost' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.invisible,
      }),
    },
    // Colour
    {
      props: { color: 'primary', variant: 'contained' },
      style: ({ theme }) => colourStyle(theme, 'contained', 'primary'),
    },
    {
      props: { color: 'primary', variant: 'ghost' },
      style: ({ theme }) => colourStyle(theme, 'ghost', 'primary'),
    },
    {
      props: { color: 'secondary', variant: 'contained' },
      style: ({ theme }) => colourStyle(theme, 'contained', 'secondary'),
    },
    {
      props: { color: 'secondary', variant: 'ghost' },
      style: ({ theme }) => colourStyle(theme, 'ghost', 'secondary'),
    },
    {
      props: { color: 'error', variant: 'contained' },
      style: ({ theme }) => colourStyle(theme, 'contained', 'error'),
    },
    {
      props: { color: 'error', variant: 'ghost' },
      style: ({ theme }) => colourStyle(theme, 'ghost', 'error'),
    },
    // Size
    {
      props: { size: 'large' },
      style: ({ theme }) => iconButtonSizeStyles(theme).large,
    },
    {
      props: { size: 'medium' },
      style: ({ theme }) => iconButtonSizeStyles(theme).medium,
    },
    {
      props: { size: 'small' },
      style: ({ theme }) => iconButtonSizeStyles(theme).small,
    },
  ],
}
